import Dropzone from 'dropzone'
import Gallery from 'blueimp-gallery'
import { fullbodyDefault as fullbodyTemplate, chat as chatTemplate } from '@/htmlBuild/dropzoneFilePreview'
import lang from '@/plugins/lang'
import { iconFile } from '@/htmlBuild/icon'
import {fireConfirmDelete} from '@/plugins/sweetalert2'

function appendOwner(name, container) {
    let el = container.querySelector('[data-dz-owner]')

    if (!name || !el) return

    el.textContent = lang.t('dropzone:sent_by', { replace: { name } })
}

export function getFullBodyTemplate() {
    return {
        thumbnailWidth: 40,
        thumbnailHeight: 40,
        addRemoveLinks: false,
        clickable: '.add-files',
        previewsContainer: '#file-previews',
        previewTemplate: fullbodyTemplate(),
        fileIconSize: '50px',
    }
}

export function getChatTemplate() {
    return {
        thumbnailWidth: 40,
        thumbnailHeight: 40,
        addRemoveLinks: false,
        clickable: '.add-files',
        previewsContainer: '#file-previews',
        previewTemplate: chatTemplate(),
        fileIconSize: '35px',
    }
}

export default class DropzoneWrap {
    constructor(selector, options = {}) {
        let inputToken = document.querySelector('meta[name="csrf-token"]')

        this.responses = {}

        this.promiseResolve = null

        this.dropzoneReady = true

        this.dropzoneOptions = {
            parallelUploads: 10,
            addRemoveLinks: true,
            headers: { 'X-CSRF-TOKEN': inputToken?.getAttribute('content') }
        }

        if (options.headers) {
            options.headers = { ...this.dropzoneOptions.headers, ...options.headers }
        }

        if (options.addDownloadAll) {
            this.#createBtnDownloadAll()

            delete options.addDownloadAll
        }

        let removeFileAjax = options.removeFileAjax

        if (options.removeFileAjax) {
            delete options.removeFileAjax
        }

        this.dropzoneOptions = {
            ...this.dropzoneOptions,
            ...options
        }

        this.dropzone = new Dropzone(selector, this.dropzoneOptions);

        if (this.btnDownloadAll) {
            this.dropzone.previewsContainer.prepend(this.btnDownloadAll)

            this.dropzone.on('addedfile', () => {
                this.btnDownloadAll.classList.remove('d-hidden')
            })

            this.dropzone.on('removedfile', () => {
                if (this.dropzone.files.length === 0) {
                    this.btnDownloadAll.classList.add('d-hidden')
                }
            })
        }

        if (typeof removeFileAjax === 'function') {
            let originalRemoveFile = this.dropzone.removeFile

            this.dropzone.removeFile = async function(file) {
                if (! file.fileId) {
                    originalRemoveFile.call(this, file)
                    return
                }

                fireConfirmDelete({
                    nameItem: file.name,
                    preConfirm: () => removeFileAjax(file).then(() => originalRemoveFile.call(this, file))
                })
            }
        }

        this.dropzone.on('success', (file, response) => {
            file.fileId = response.id
            file.url = response.url

            this.#bindFileDownload(file)

            appendOwner(response.author?.name, file.previewElement)

            this.responses[file.upload.uuid] = {
                name: file.name,
                type: file.type,
                size: file.size,
                response: response
            }
        });

        this.dropzone.on('queuecomplete', () => {
            this.dropzoneReady = true

            if (this.promiseResolve) {
                this.promiseResolve(this.responses)
                this.promiseResolve = null
            }
        })

        this.dropzone.on('error', (file, message, xhr) => {
            if(!xhr) return;

            let container = file.previewElement.querySelector('.dz-error-message')
            container.textContent = ''

            if (xhr.status === 422) {
                 for (let field in message.errors) {
                    message.errors[field].forEach(err => {
                        let divText = document.createElement('div')
                        divText.textContent = err
                        container.appendChild(divText)
                    })
                }

                return
            }

            container.textContent = lang.t('dropzone:error_to_send_file')
        })

        this.dropzone.on('removedfile', file => {
            if (file.upload?.uuid) {
                delete this.responses[file.upload.uuid]
            }
        })

        this.dropzone.on('canceled', file => {
            if (file.upload?.uuid) {
                delete this.responses[file.upload.uuid]
            }
        })

        this.dropzone.on('addedfile', file => {
            this.dropzoneReady = false

            let options = this.dropzone.options

            if (
                options.createImageThumbnails &&
                file.type.match(/image.*/) &&
                file.size <= options.maxThumbnailFilesize * 1024 * 1024
            )
            {
                file.previewElement.querySelector('.file-thumbnail').classList.remove('d-none')
            } else {
                let icon = iconFile(file.type)
                icon.style.fontSize = this.dropzoneOptions.fileIconSize
                icon.style.lineHeight = 'normal'
                file.previewElement.querySelector('.box-icon-file').prepend(icon);
            }

            this.#bindFileDownload(file)
        })
    }

    addExistingFile(mockFile) {
        this.dropzone.emit("addedfile", mockFile)

        this.dropzone.emit("complete", mockFile)

        if (mockFile.type.match(/image.*/)) {
            this.dropzone.emit("thumbnail", mockFile, mockFile.url)
        }

        appendOwner(mockFile.owner, mockFile.previewElement)

        this.dropzone.files.push(mockFile)
    }

    /**
     * A promise será resolvida quando todos os files estiverem pronto (não estiverem em queue ou uploading)
     *
     * @return Promise<Object>
     */
    waitUntilReady() {
        return new Promise((resolve) => {
            if (this.dropzoneReady) {
                resolve(this.responses)
            } else {
                this.promiseResolve = resolve
            }
        })
    }

    #createBtnDownloadAll() {
        this.btnDownloadAll = document.createElement('div')
        this.btnDownloadAll.className = 'd-hidden ml-1'

        let icon = document.createElement('i')
        icon.className = 'fa-solid fa-cloud-arrow-down mr-2'

        let btn = document.createElement('a')
        btn.className = 'text-blue'
        btn.href = '#'
        btn.textContent = lang.t('dropzone:download_all_files')

        btn.prepend(icon)

        btn.addEventListener('click',(e) => {
            e.preventDefault()

            this.btnDownloadAll.parentElement.querySelectorAll('.dz-download').forEach((item, i) => {
                setTimeout(() => {
                    let link = document.createElement('a');
                    link.href = item.href;
                    link.click();
                }, 1000 * i)
            })
        })

        this.btnDownloadAll.append(btn)
    }

    #bindFileDownload(file) {
        let downloadEl = file.previewElement.querySelector('.dz-download')

        if (file.url && downloadEl) {
            downloadEl.href = file.url

            if (file.type.match(/image.*/)) {
                let that = this

                file.previewElement.addEventListener('click', function(event) {
                    event.preventDefault()

                    that.openGallery(this.querySelector('.dz-download'))
                })
            } else {
                file.previewElement.addEventListener('click', function(event) {
                    event.preventDefault()

                    let link = document.createElement('a');
                    link.href = this.querySelector('.dz-download').href;
                    link.click();
                })
            }
        }
    }

    openGallery(el) {
        let urls = []

        let index = 0

        this.dropzone.files.forEach(file => {
            if (file.url && file.type.match(/image.*/)) {
                let count = urls.push(file.url)

                if (file.url === (el.href || el.src)) {
                    index = count - 1
                }
            }
        })

        if (urls.length === 0) {
            return
        }

        Gallery(urls, { index })
    }
}
